import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  SemanticPanel                                                      */
/*  Colour-coded container for insight modules.                        */
/*  Uses --sf-{danger|info|success|warning}-* tokens.                  */
/* ------------------------------------------------------------------ */

export type SemanticPanelVariant = "danger" | "info" | "success" | "warning";

export interface SemanticPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: SemanticPanelVariant;
  /** Icon element rendered before the title. */
  icon?: React.ReactNode;
  /** Panel heading text. */
  title?: string;
  /** Optional element rendered on the right side of the header row. */
  headerRight?: React.ReactNode;
}

const css = /* css */ `
  .sf-semantic-panel {
    border-radius: var(--sf-radius-lg);
    border-left: 5px solid transparent;
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Variant: danger */
  .sf-semantic-panel--danger {
    background: var(--sf-danger-bg);
    border-left-color: var(--sf-danger-border);
  }

  /* Variant: info */
  .sf-semantic-panel--info {
    background: var(--sf-info-bg);
    border-left-color: var(--sf-info-border);
  }

  /* Variant: success */
  .sf-semantic-panel--success {
    background: var(--sf-success-bg);
    border-left-color: var(--sf-success-border);
  }

  /* Variant: warning */
  .sf-semantic-panel--warning {
    background: var(--sf-warning-bg);
    border-left-color: var(--sf-warning-border);
  }

  /* Header row */
  .sf-semantic-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    min-height: 1.5rem;
  }

  .sf-semantic-panel__header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .sf-semantic-panel__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    width: 18px;
    height: 18px;
  }
  .sf-semantic-panel--danger .sf-semantic-panel__icon { color: var(--sf-danger); }
  .sf-semantic-panel--info   .sf-semantic-panel__icon { color: var(--sf-info); }
  .sf-semantic-panel--success .sf-semantic-panel__icon { color: var(--sf-success); }
  .sf-semantic-panel--warning .sf-semantic-panel__icon { color: var(--sf-warning); }

  .sf-semantic-panel__title {
    font-size: var(--sf-text-sm);
    font-weight: var(--sf-weight-semibold);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--sf-text);
  }
  .sf-semantic-panel--danger  .sf-semantic-panel__title { color: var(--sf-danger); }
  .sf-semantic-panel--info    .sf-semantic-panel__title { color: var(--sf-info); }
  .sf-semantic-panel--success .sf-semantic-panel__title { color: var(--sf-success); }
  .sf-semantic-panel--warning .sf-semantic-panel__title { color: var(--sf-warning); }

  /* Body */
  .sf-semantic-panel__body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const SemanticPanel = React.forwardRef<HTMLDivElement, SemanticPanelProps>(
  ({ variant, icon, title, headerRight, children, className, ...rest }, ref) => {
    injectStyles("sf-semantic-panel", css);

    const classes = [
      "sf-semantic-panel",
      `sf-semantic-panel--${variant}`,
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const showHeader = icon || title || headerRight;

    return (
      <div ref={ref} className={classes} {...rest}>
        {showHeader && (
          <div className="sf-semantic-panel__header">
            <div className="sf-semantic-panel__header-left">
              {icon && <span className="sf-semantic-panel__icon" aria-hidden="true">{icon}</span>}
              {title && <span className="sf-semantic-panel__title">{title}</span>}
            </div>
            {headerRight}
          </div>
        )}
        <div className="sf-semantic-panel__body">{children}</div>
      </div>
    );
  },
);

SemanticPanel.displayName = "SemanticPanel";
export { SemanticPanel };
